/**
 * The sign-in page status message shown by `<tb-login-screen>` (input `notice`).
 *
 * Source of truth: edge-bff `GET /bff/config/login-notice`, which reads the Unleash flag
 * `toybank.login.status-message` (an operator-authored JSON strategy-variant payload) and
 * validates it server-side (LoginNoticePayload). The browser re-checks the SAME rules here,
 * where the value is rendered, so a host can never pass the component something the server would
 * have refused -- including a hand-built notice such as the debug wrapper's default.
 *
 * Rules (mirrors LoginNoticePayload; any violation rejects the WHOLE notice, never a partial one):
 *   - severity: optional, default `info`; exactly `info`, `warning` or `critical` (lower case).
 *   - text: required; trimmed length 1-300 code points; no ISO control characters (newline and
 *     tab included), no bidi controls (U+200E, U+200F, U+202A-U+202E, U+2066-U+2069, U+061C), no
 *     other format characters (Unicode Cf: zero-width characters, U+FEFF, U+00AD, the tag
 *     characters) except U+200D for emoji sequences, no lone surrogates, and at least one visible
 *     code point.
 *   - links: optional, default `[]`; 0-3 objects of `{ label, href }`.
 *     - label: trimmed, 1-60 code points, same character rules as text.
 *     - href: trimmed, 1-512 chars, no whitespace, control or bidi characters, and one of:
 *       a same-origin path (`/x`, never `//x` or a backslash), `https://` to an ASCII host with
 *       no userinfo, or `http://` only to `localhost` / `*.localhost`. Scheme case is ignored.
 *   - expiresAt (the server never echoes it; checked if present): an ISO-8601 instant with `Z`
 *     or an offset; malformed or already past means no notice.
 * Unknown keys are ignored (the server already refuses them in the authored payload).
 *
 * Rendered as text and a bound `[href]` only -- never innerHTML.
 */
export type LoginNoticeSeverity = 'info' | 'warning' | 'critical';

export interface LoginNoticeLink {
  readonly label: string;
  readonly href: string;
}

export interface LoginNotice {
  readonly severity: LoginNoticeSeverity;
  readonly text: string;
  readonly links: readonly LoginNoticeLink[];
}

const SEVERITIES: readonly LoginNoticeSeverity[] = ['info', 'warning', 'critical'];
const TEXT_MAX_CODE_POINTS = 300;
const LABEL_MAX_CODE_POINTS = 60;
const HREF_MAX_CHARS = 512;
const LINKS_MAX = 3;

// ISO control characters (C0, DEL, C1), U+061C, every format character (Cf: the bidi marks,
// zero-width characters, U+FEFF, U+00AD, the tag characters) except U+200D, and lone surrogates
// (Cs: a valid pair is one code point under the u flag, so only an unpaired half matches).
const FORBIDDEN_TEXT_CHARS = /[\u0000-\u001f\u007f-\u009f\u061c]|(?!\u200d)\p{Cf}|\p{Cs}/u;
// The same, plus whitespace and U+200D (an href has no emoji sequences).
const FORBIDDEN_HREF_CHARS = /[\s\u0000-\u001f\u007f-\u009f\u061c\u200d]|\p{Cf}|\p{Cs}/u;
// Code points that draw nothing on their own (mirrors LoginNoticePayload.isInvisible): whitespace,
// U+200D, the Hangul fillers, the blank Braille pattern and the variation selectors.
const INVISIBLE_ONLY = /^[\s\u200d\u115f\u1160\u3164\uffa0\u2800\ufe00-\ufe0f\u{e0100}-\u{e01ef}]*$/u;
// Java String.strip() semantics (Character.isWhitespace), so a value trims exactly as the server
// trims it: unlike String.prototype.trim, U+00A0, U+2007, U+202F and U+FEFF are NOT stripped.
const STRIP_EDGES = /^[\t\n\u000b\f\r\u001c-\u001f \u1680\u2000-\u2006\u2008-\u200a\u2028\u2029\u205f\u3000]+|[\t\n\u000b\f\r\u001c-\u001f \u1680\u2000-\u2006\u2008-\u200a\u2028\u2029\u205f\u3000]+$/gu;
const SAME_ORIGIN_PATH = /^\/(?![/\x5c])[^\s\x5c]*$/;
const HTTPS_URL = /^https:\/\/[A-Za-z0-9.-]+(?::\d{1,5})?(?:[/?#][^\s\x5c]*)?$/i;
const HTTP_LOCALHOST_URL = /^http:\/\/(?:[A-Za-z0-9.-]+\.)?localhost(?::\d{1,5})?(?:[/?#][^\s\x5c]*)?$/i;
const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,9})?)?(?:Z|[+-]\d{2}:\d{2})$/i;

function strip(value: string): string {
  return value.replace(STRIP_EDGES, '');
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function cleanText(value: unknown, maxCodePoints: number): string | null {
  if (typeof value !== 'string' || FORBIDDEN_TEXT_CHARS.test(value)) return null;
  const trimmed = strip(value);
  const length = Array.from(trimmed).length;
  if (length < 1 || length > maxCodePoints || INVISIBLE_ONLY.test(trimmed)) return null;
  return trimmed;
}

function isAllowedHref(href: string): boolean {
  if (href.length < 1 || href.length > HREF_MAX_CHARS || FORBIDDEN_HREF_CHARS.test(href)) return false;
  return SAME_ORIGIN_PATH.test(href) || HTTPS_URL.test(href) || HTTP_LOCALHOST_URL.test(href);
}

function cleanLink(value: unknown): LoginNoticeLink | null {
  if (!isRecord(value)) return null;
  const label = cleanText(value['label'], LABEL_MAX_CODE_POINTS);
  const hrefRaw = value['href'];
  if (label === null || typeof hrefRaw !== 'string') return null;
  const href = strip(hrefRaw);
  return isAllowedHref(href) ? { label, href } : null;
}

/** false = absent or still in the future; true = expired; null = malformed. */
function expired(value: unknown): boolean | null {
  if (value === undefined) return false;
  if (typeof value !== 'string' || !ISO_INSTANT.test(value)) return null;
  const at = Date.parse(value);
  return Number.isNaN(at) ? null : Date.now() >= at;
}

/**
 * Validates and normalises a login notice. Returns null for anything that is not exactly a valid
 * notice (fails closed): a wrong shape, a single bad field, or an `expiresAt` that is malformed or
 * already past.
 */
export function parseLoginNotice(raw: unknown): LoginNotice | null {
  if (!isRecord(raw)) return null;

  const severityRaw = raw['severity'];
  const severity: LoginNoticeSeverity | undefined =
    severityRaw === undefined ? 'info' : SEVERITIES.find((s) => s === severityRaw);
  if (!severity) return null;

  const text = cleanText(raw['text'], TEXT_MAX_CODE_POINTS);
  if (text === null) return null;

  const linksRaw = raw['links'] === undefined ? [] : raw['links'];
  if (!Array.isArray(linksRaw) || linksRaw.length > LINKS_MAX) return null;
  const links: LoginNoticeLink[] = [];
  for (const entry of linksRaw) {
    const link = cleanLink(entry);
    if (!link) return null;
    links.push(link);
  }

  if (expired(raw['expiresAt']) !== false) return null;

  return { severity, text, links };
}
